import React, { useEffect, useState, useMemo } from "react";
import { createPortal } from "react-dom";
import PrimaryButton from "../../PrimaryButton";
import Confetti from "../../Confetti/Confetti";
import MobileViews from "./MobileViews";
import {
  approveOrRejectResources,
  createOffer,
  getNearestOffersByMerchant,
  getOffersByMerchant,
} from "../../../../services/offerService";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { getLocationById } from "../../../../services/franchiseService";
import toast from "../../../../utils/toast";
import { useOfferFormContext } from "../OfferFormContext";
import { getLiveOfferSaveFlags, isLiveOfferStatus } from "../offerApprovalHelpers";

const OfferReview = ({
  handlePrevious,
  editOfferData,
  setPayloadData,
  selectedMerchant,
  handleSaveAndExit,
  payload,
  isApproved: isApprovedProp,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const offerId = useSelector(
    (state) => state?.offerReducer?.createdOffer?._id
  );
  const role = useSelector(
    (state) => state?.authReducer?.tokenData?.user?.role
  );
  const roleLower = (role || "").toLowerCase();
  const isAdminRole = roleLower === "admin";
  const isMerchantRole = roleLower === "merchant";
  const loading = useSelector((state) => state?.offerReducer?.loading);
  const createdOfferFromRedux = useSelector(
    (state) => state?.offerReducer?.createdOffer
  );

  const [offerData, setOfferData] = useState(null);
  const [firstLocation, setFirstLocation] = useState(null);
  const [merchantData, setMerchantData] = useState(null);
  const [showCelebration, setShowCelebration] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  // removed duplicates
  const [isFirstOffer, setIsFirstOffer] = useState(false);

  const {
    setNextLabel,
    registerNextAction,
    setIsNextLoading,
  } = useOfferFormContext();

  const editStatus = (editOfferData?.status || offerData?.status || "").toLowerCase();
  const isLiveEdit = isLiveOfferStatus(editStatus);
  const liveEditPreview = {
    ...(editOfferData || offerData || {}),
    ...(offerData || {}),
    ...(payload || {}),
  };
  const liveSaveFlags = getLiveOfferSaveFlags({
    currentPayload: liveEditPreview,
    originalOffer: editOfferData || offerData,
    isLive: isLiveEdit,
    fallbackIsCreated: editOfferData?.isCreated ?? false,
    fallbackStatus: isLiveEdit ? "live" : null,
  });
  const wasPreviouslySubmitted =
    editOfferData &&
    editStatus !== "drafted" &&
    editOfferData?.isCreated !== false;

  useEffect(() => {
    setNextLabel(
      isLiveEdit
        ? (liveSaveFlags.preserveLiveOffer ? "Save & Exit" : "Resubmit")
        : (wasPreviouslySubmitted ? "Resubmit" : "Submit")
    );
    return () => setNextLabel("Next");
  }, [setNextLabel, isLiveEdit, wasPreviouslySubmitted, liveSaveFlags.preserveLiveOffer]);

  const getLocationData = async () => {
    const locId = offerData?.tempLocations?.[0]?._id;
    if (!locId) return;

    const res = await dispatch(getLocationById({ id: locId, silent: true }))?.unwrap();
    if (res?.success) {
      setFirstLocation(res.data);
    }
  };

  const getMerchantData = async () => {
    const payload = {
      lat: firstLocation?.googlePlaceId?.lat,
      lng: firstLocation?.googlePlaceId?.lng,
      merchantId: offerData?.merchant?._id,
    };
    const res = await dispatch(getNearestOffersByMerchant({ ...payload, silent: true }))?.unwrap();
    if (res?.success) {
      setMerchantData(res.data);
    }
  };

  const effectiveMerchantId =
    selectedMerchant?._id ||
    offerData?.merchant?._id ||
    offerData?.merchantId ||
    editOfferData?.merchant?._id ||
    editOfferData?.merchantId ||
    (typeof offerData?.merchant === "string" ? offerData.merchant : null) ||
    (typeof editOfferData?.merchant === "string" ? editOfferData.merchant : null) ||
    null;

  const currentOfferId = offerData?._id || editOfferData?._id || offerId || location.state?.id;
  const isLive =
    typeof isApprovedProp === "boolean"
      ? isApprovedProp
      : isLiveOfferStatus(editOfferData?.status || offerData?.status);
  const isDirty = useMemo(() => {
    if (!editOfferData) return false;

    const initialOfferCodes = [...(editOfferData?.offerCodes || [])].sort().join(",");
    const currentOfferCodes = [...(offerData?.offerCodes || [])].sort().join(",");
    const initialRuleIds = [...(editOfferData?.ruleAndRegulation || [])]
      .map((r) => (typeof r === "object" ? r?._id : r))
      .sort()
      .join(",");
    const currentRuleIds = [...(offerData?.ruleAndRegulation || [])]
      .map((r) => (typeof r === "object" ? r?._id : r))
      .sort()
      .join(",");
    const initialLocationIds = [...(editOfferData?.tempLocations || editOfferData?.locations || [])]
      .map((l) => (typeof l === "object" ? l?._id : l))
      .sort()
      .join(",");
    const currentLocationIds = [...(offerData?.tempLocations || offerData?.locations || [])]
      .map((l) => (typeof l === "object" ? l?._id : l))
      .sort()
      .join(",");

    return (
      (editOfferData?.title || "") !== (offerData?.title || "") ||
      (editOfferData?.offerType || "") !== (offerData?.offerType || "") ||
      (editOfferData?.offerDiscountType || "") !== (offerData?.offerDiscountType || "") ||
      (editOfferData?.onlineOption || "") !== (offerData?.onlineOption || "") ||
      (editOfferData?.discountRange || "") !== (offerData?.discountRange || "") ||
      (editOfferData?.fixedDollarAmount || "") !== (offerData?.fixedDollarAmount || "") ||
      (editOfferData?.websiteURL || "") !== (offerData?.websiteURL || "") ||
      (editOfferData?.description || "").trim() !== (offerData?.description || "").trim() ||
      initialOfferCodes !== currentOfferCodes ||
      initialRuleIds !== currentRuleIds ||
      initialLocationIds !== currentLocationIds
    );
  }, [editOfferData, offerData]);

  // Check if this is the merchant's first offer
  const checkIfFirstOffer = async () => {
    // Don't check for first offer if editing an existing offer
    if (editOfferData) {
      setIsFirstOffer(false);
      return;
    }

    // Get the merchant ID - for admin, use selectedMerchant; for merchant, use offerData merchant
    const merchantId = effectiveMerchantId;

    if (!merchantId) {
      setIsFirstOffer(false);
      return;
    }

    try {
      // Fetch merchant's offers to check if they have any completed offers
      const res = await dispatch(
        getOffersByMerchant({
          merchantId: merchantId,
          limit: 100, // Get a reasonable number to check
          page: 1,
          silent: true,
        })
      )?.unwrap();

      if (res?.success) {
        // Filter for completed offers (isCreated: true) excluding the current offer
        const existingOffers = res?.data || [];
        const completedOffers = existingOffers.filter(
          (offer) => offer?.isCreated === true && offer?._id !== offerData?._id
        );

        // This is the first offer if there are no existing completed offers
        setIsFirstOffer(completedOffers.length === 0);
      } else {
        setIsFirstOffer(false);
      }
    } catch (error) {
      // If error occurs, default to not showing celebration
      console.warn("Error checking if first offer:", error);
      setIsFirstOffer(false);
    }
  };

  useEffect(() => {
    const currentViewId = offerId || editOfferData?._id || location.state?.id;

    if (createdOfferFromRedux && createdOfferFromRedux._id === currentViewId) {
      setOfferData(createdOfferFromRedux);
      return;
    }

    if (editOfferData) {
      setOfferData(editOfferData);
    }
  }, [createdOfferFromRedux, editOfferData, offerId, location.state?.id]);

  useEffect(() => {
    if (offerData) {
      getLocationData();
    }
  }, [offerData]);

  useEffect(() => {
    if (offerData && firstLocation) {
      getMerchantData();
    }
  }, [firstLocation]);

  // Check if this is the first offer when offerData is available
  useEffect(() => {
    if (offerData && !editOfferData && role !== "admin") {
      checkIfFirstOffer();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offerData, editOfferData, role]);

  useEffect(() => {
    if (!offerData) return;
    const payload = {
      merchantId: effectiveMerchantId,
      category: offerData?.category?._id || offerData?.category,
      subCategory: offerData?.subCategory?.map((s) => s?._id || s),
      title: offerData?.title,
      offerType: offerData?.offerType,
      discountRange: offerData?.discountRange,
      moneySaved: offerData?.moneySaved,
      thumbnailImage: offerData?.thumbnailImage,
      previewImages: offerData?.previewImages,
      description: offerData?.description,
      startDate: offerData?.startDate,
      endDate: offerData?.endDate,
      redemptionDays: offerData?.redemptionFrequency === "ONCE_IN_12_MONTHS" ? undefined : offerData?.redemptionDays,
      ruleAndRegulation: offerData?.ruleAndRegulation?.map((r) => r?._id),
      locations: offerData?.tempLocations?.map((l) => l?._id),
      discountTitle: offerData?.discountTitle,
      id: offerData?._id,
      // Keep live offers live during review/intermediate steps.
      isCreated: isLive ? true : false,
      ...(isLive ? { status: "live" } : {}),
    };
    setPayloadData(payload);
  }, [offerData, isLive, setPayloadData, effectiveMerchantId]);

  const handleSubmit = async () => {
    const finalOfferCodes =
      createdOfferFromRedux?.offerCodes || offerData?.offerCodes;

    const resolvedOnlineOption =
      typeof offerData?.onlineOption === "string"
        ? offerData?.onlineOption
        : (Array.isArray(finalOfferCodes) && finalOfferCodes.length > 0)
          ? "ONLINE_WITH_CODE"
          : "ONLINE";

    const shouldMaintainLive = (isMerchantRole && isLive && liveSaveFlags.preserveLiveOffer);

    const payload = {
      merchantId: effectiveMerchantId,
      category: offerData?.category?._id || offerData?.category,
      subCategory: (offerData?.subCategory || [])?.map((s) => s?._id || s),
      title: offerData?.title,
      offerType: offerData?.offerType,
      offerDiscountType: offerData?.offerDiscountType,
      offerCodes: finalOfferCodes,
      websiteURL: offerData?.websiteURL,
      ...(offerData?.offerType !== "INSTORE_SWOODLE_OFFER" && offerData?.offerType !== "INSTORE_OFFER"
        ? { onlineOption: resolvedOnlineOption }
        : {}), // Only include onlineOption for non-instore offers
      necessaryRule: offerData?.necessaryRule,
      ...(offerData?.redemptionFrequency
        ? { redemptionFrequency: offerData?.redemptionFrequency }
        : {}),
      redemptionDays: offerData?.redemptionFrequency === "ONCE_IN_12_MONTHS" ? undefined : offerData?.redemptionDays,
      availableAcrossAllStores: offerData?.availableAcrossAllStores,
      discountRange: offerData?.discountRange,
      fixedDollarAmount: offerData?.fixedDollarAmount,
      thumbnailImage: offerData?.thumbnailImage,
      previewImages: offerData?.previewImages,
      ...(offerData?.description?.trim()
        ? { description: offerData?.description?.trim() }
        : {}),
      redemptionMethod: offerData?.redemptionMethod,
      startTime: offerData?.startTime,
      endTime: offerData?.endTime,
      callback_url: offerData?.callback_url,
      ruleAndRegulation: (offerData?.ruleAndRegulation || [])?.map((r) => r?._id || r),
      locations: (offerData?.tempLocations || offerData?.locations || [])?.map((l) => l?._id || l),
      discountTitle: offerData?.discountTitle,
      startDate: offerData?.startDate,
      endDate: offerData?.endDate,
      id: currentOfferId,
      // If merchant editing live with minor changes, keep it live.
      isCreated: true, 
      ...(shouldMaintainLive ? { status: "live" } : {}),
    };

    // Payload flags already set above

    if (!offerData?.description || `${offerData?.description}`.trim().length === 0) {
      delete payload.description;
    }

    try {
      const createRes = await dispatch(
        createOffer({
          ...payload,
          offerId: currentOfferId,
        })
      )?.unwrap();

      if (!createRes?.success) return;

      if (isAdminRole) {
        const statusRes = await dispatch(
          approveOrRejectResources({
            type: "offer",
            status: "live",
            ids: [offerData?._id],
            merchantId: selectedMerchant?._id,
          })
        )?.unwrap();
        if (statusRes?.success) navigate("/admin/offer-list");
        return;
      }

      // Always show the success modal
      setShowSuccessModal(true);
      // Only show celebration (confetti) if this is the first offer
      if (isFirstOffer) {
        setShowCelebration(true);
      } else {
        setShowCelebration(false);
      }
    } catch (error) {
      const message =
        error?.message ||
        error?.data?.message ||
        "We couldn't submit your offer just yet. Please review the required fields and try again.";
      toast.error(message);
    }
  };

  useEffect(() => {
    let timer;
    if (showCelebration) {
      timer = setTimeout(() => {
        setShowCelebration(false);
      }, 5000);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [showCelebration]);

  const handleCloseCelebration = () => {
    setShowCelebration(false);
    setShowSuccessModal(false);
    navigate(role === "admin" ? "/admin/offer-list" : "/merchant/offer-list");
  };

  // Register the Next (now Submit) action
  useEffect(() => {
    registerNextAction(() => handleSubmit());
  }, [registerNextAction, offerData, editOfferData, selectedMerchant, isAdminRole, isMerchantRole, isFirstOffer, isDirty, isLive]); // Dependencies crucial for handleSubmit closure

  // Sync loading state
  useEffect(() => {
    setIsNextLoading(loading);
  }, [loading, setIsNextLoading]);

  return (
    <div className="w-full flex flex-col items-center justify-center mt-28 sm:mt-20 pb-20">
      <div className="w-full max-w-[1100px] relative flex flex-col items-center">
        <div className="w-[90%] sm:w-[100%] md:w-[95%] lg:w-[90%] xl:w-[85%] offerform">
          <h2 className="text-[26px] sm:text-[32px] font-[600] text-[#00ADEF] font-poppins text-center mb-4 sm:mb-1">
            Review and Save <span className="text-[#303030]">Your Offer</span>
          </h2>
          <p className="font-poppins font-[400] sm:text-[10px] md:text-[12px] text-[#767676] mb-10 text-center">
            You're almost done! Check your details and make sure everything is accurate.
          </p>
          <MobileViews offerData={offerData} merchantData={merchantData} selectedMerchant={selectedMerchant} />
        </div>
      </div>

      {/* Show confetti only for first offer - Portalled to body */}
      {showCelebration && createPortal(<Confetti />, document.body)}

      {/* Always show success modal for all offers - Portalled to body to cover Sidebar */}
      {showSuccessModal &&
        createPortal(
          <div
            className="offer-success-modal-backdrop"
            onClick={handleCloseCelebration}
          >
            <div
              className="offer-success-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <h3>Offer Submitted!</h3>
              <p className="offer-success-message">
                Your offer has been sent to admin. Once the offer has been
                live, you'll receive an email notification.
              </p>
              <div className="offer-success-actions">
                <PrimaryButton
                  className="offer-success-ok-btn w-[60%] h-[45px] rounded-[8px] text-[16px] font-[500] font-montserrat"
                  label="OK"
                  onClick={handleCloseCelebration}
                />
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default OfferReview;
